import { EBool } from '~/src/dash/api/types/bool';
import { ISimpleLineItem } from '~/src/dash/apiV2/campaigns';
import {
  generateAdvertiserOptionsList,
  generateOrderOptionsList,
} from '~/src/dash/apiV2/campaigns/filters';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoSingleNumber,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import {
  CampaignStatusOptions,
  CampaignStatusPresets,
  DebugPrebidDataOptions,
  EDebugPrebidData,
} from '~/src/dash/components/filters/options/campaigns';
import { EStatus, StatusOptions } from '~/src/dash/components/filters/status';
import {
  IListViewTableColumns,
  IListViewTablePagination,
  IListViewTableRow,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  DateTimeRenderUnchanged,
  EllipsisWithTooltipRender,
  MultiCurrencyRender,
  PercentRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import {
  CampaignStatusRender,
  OrderRender,
} from '~/src/dash/views/campaigns/line-items/LineItemsListView/common';

export interface ILineItemsListViewConfig {
  cypressClassName: string;
  startToEndDateRanges: IStartToEndDateRanges;
  timezone: string;
  filters: Record<string, IExtendedIFilterRowObject>;
  columns: IListViewTableColumns;
  table: IListViewTablePagination;
  activeColumnKeys: string[];
  columnSelector: {
    resetKeys: string[];
  };
}

export const lineItemsListViewConfig: ILineItemsListViewConfig = {
  cypressClassName: 'lineitemslistview',
  startToEndDateRanges: {
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  },
  timezone: '',
  filters: {
    Status: {
      key: 'Status',
      tKey: 'Status',
      getList: () => generateOptionsListAsPromise(StatusOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([EStatus.Active]),
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([EStatus.ShowAll]),
      queryKey: 'active',
      formatQueryValue: convertSetIntoSingleNumber,
    },
    Orders: {
      key: 'Orders',
      tKey: 'Orders',
      getList: generateOrderOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'order_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
      enableActiveFilter: true,
      onLoadDisplayActiveOnly: true,
    },
    Advertisers: {
      key: 'Advertisers',
      tKey: 'Advertisers',
      getList: generateAdvertiserOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'advertiser_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    CampaignStatus: {
      key: 'CampaignStatus',
      tKey: 'CampaignStatus',
      getList: () => generateOptionsListAsPromise(CampaignStatusOptions),
      disableSearch: true,
      disableSort: true,
      queryKey: 'status',
      formatQueryValue: convertSetIntoArrayOfNumbers,
      presets: CampaignStatusPresets,
    },
    Debug: {
      key: 'Debug',
      tKey: 'DebugPrebidDataWithAsterisk',
      isSuperUserOnly: true,
      getList: () => generateOptionsListAsPromise(DebugPrebidDataOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([EDebugPrebidData.ShowAll]), // Show All
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([EDebugPrebidData.ShowAll]), // Show All
      queryKey: 'debug',
      formatQueryValue: convertSetIntoSingleNumber,
    },
  },
  table: {
    page: 0,
    rowsPerPage: 10,
    order: 'asc',
    orderBy: 'start_date',
  },
  activeColumnKeys: [
    'Order',
    'CampaignStatus',
    'StartDate',
    'EndDate',
    'Advertiser',
    'CPM',
    'Impressions',
    'Goal',
    'Pacing',
  ],
  columnSelector: {
    resetKeys: [
      'Order',
      'CampaignStatus',
      'StartDate',
      'EndDate',
      'Advertiser',
      'CPM',
      'Impressions',
      'Goal',
      'Pacing',
    ],
  },
  columns: {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: true,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Status: {
      defaultOrderDirection: 'desc',
      formatValue: (value: number) => value === (EBool.EBool_TRUE as number),
      headerRenderType: 'switch',
      key: 'Status',
      tKey: 'Active',
      label: 'Active',
      requestKey: 'active',
      responseKey: 'Active',
      valueType: 'boolean',
      orderByKey: 'active',
    },
    Name: {
      cellJSX: EllipsisWithTooltipRender,
      cellRenderType: 'name_link',
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: 'Name',
      tKey: 'Name',
      requestKey: 'name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'name',
    },
    Order: {
      cellJSX: OrderRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Order',
      label: 'Order',
      tKey: 'Order',
      orderByKey: 'order_name',
      requestKey: '',
      responseKey: 'OrderName',
      valueType: 'string',
    },
    CampaignStatus: {
      cellJSX: CampaignStatusRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'CampaignStatus',
      label: 'Status',
      tKey: 'Status',
      requestKey: 'status',
      responseKey: 'Status',
      valueType: 'number',
      orderByKey: 'status',
    },
    StartDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'StartDate',
      label: 'Start Date',
      tKey: 'StartDate',
      orderByKey: 'start_date',
      requestKey: '',
      responseKey: 'StartDate',
      valueType: 'date',
    },
    EndDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'EndDate',
      label: 'End Date',
      tKey: 'EndDate',
      orderByKey: 'end_date',
      requestKey: '',
      responseKey: 'EndDate',
      valueType: 'date',
    },
    Advertiser: {
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Advertiser',
      label: 'Advertiser',
      tKey: 'Advertiser',
      orderByKey: 'advertiser_name',
      requestKey: '',
      responseKey: 'AdvertiserName',
      valueType: 'string',
    },
    CPM: {
      cellJSX: MultiCurrencyRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'CPM',
      label: 'Net CPM',
      tKey: 'NetCPM',
      orderByKey: 'cpm',
      requestKey: '',
      responseKey: 'CPM',
      valueType: 'number',
    },
    Impressions: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Impressions',
      label: 'Delivered Impressions',
      tKey: 'DeliveredImpressions',
      orderByKey: 'impressions',
      requestKey: '',
      responseKey: 'DeliveredImps',
      valueType: 'number',
    },
    Goal: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Goal',
      label: 'Goal',
      tKey: 'Goal',
      orderByKey: 'goal',
      requestKey: '',
      responseKey: 'Goal',
      valueType: 'number',
    },
    Pacing: {
      cellJSX: PercentRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Pacing',
      label: 'Pacing',
      tKey: 'Pacing',
      orderByKey: 'pacing',
      requestKey: '',
      responseKey: 'Pacing',
      valueType: 'number',
    },
    Actions: {
      cellRenderType: 'actions',
      disableSort: true,
      headerRenderType: 'actions',
      key: 'Actions',
      label: 'Actions',
      tKey: 'Actions',
      requestKey: '',
      responseKey: '',
      valueType: 'component',
    },
    Revenue: {
      cellJSX: MultiCurrencyRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Revenue',
      label: 'Revenue',
      tKey: 'Revenue',
      requestKey: '',
      responseKey: 'Revenue',
      orderByKey: 'revenue',
      valueType: 'number',
    },
    UpdatedAt: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'UpdatedAt',
      label: 'Last Updated',
      tKey: 'LastUpdated',
      orderByKey: 'updated_at',
      requestKey: '',
      responseKey: 'UpdatedAt',
      valueType: 'date',
    },
    OrderID: {
      key: 'OrderID',
      label: 'Order ID',
      requestKey: '',
      responseKey: 'OrderID',
      valueType: 'number',
      isHidden: true,
    },
    CurrencySymbol: {
      key: 'CurrencySymbol',
      label: 'Currency Symbol',
      requestKey: '',
      responseKey: 'CurrencySymbol',
      valueType: 'string',
      isHidden: true,
    },
  },
};

export const formatLineItemsResponseIntoLVTRows = (
  lineItems: ISimpleLineItem[],
  cfg: IListViewTableColumns,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  lineItems.forEach((lineItem) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = lineItem[responseKey as keyof ISimpleLineItem];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};
